import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DoctorDetailCard } from './DoctorDetailCard';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../common/Loader';
import { BackButton } from '../common/BackButton';
import { PopupMultiSelect, PopupTrigger } from '../common/PopupMultiSelect';
import { SuccessPopup } from '../common/SuccessPopup';
import { getMedicalIcon } from '../../utils/medicalIcons';
import { doctorsService, Doctor } from '../../services/doctorsService';
import { createAppointmentRequest } from '../../services/requestsService';
import { useClinic } from '../../hooks/useClinic';
import { useAuthStore } from '../../stores/authStore';
import { getDoctorPhotoUrl } from '../../services/clinicService';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

interface Specialization { id: string; name: string }
interface Category { id: string; name: string }

const normalizeDoctor = (raw: any): Doctor => {
  if (!raw) return raw;
  return {
    ...raw,
    specializations: raw.specializations || raw.Specializations || [],
    branches: raw.branches || raw.Branches || [],
    categories: raw.categories || raw.Categories || [],
  };
};

// КАРТОЧКА ВРАЧА (359 x 310)
const DoctorCard: React.FC<{
  doctor: Doctor;
  onDetails: (doctor: Doctor) => void;
  onBook: (id: string) => void;
  isSubmitting: boolean;
}> = ({ doctor, onDetails, onBook, isSubmitting }) => {
  const specNames = doctor.specializations?.map(s => s.name).join(', ') || '';
  const shortDesc = doctor.description || '';

  return (
    <Box onClick={() => onDetails(doctor)} sx={{
      width: '100%',
      maxWidth: { xs: '100%', sm: '480px', md: '600px' },
      backgroundColor: '#fff',
      borderRadius: '16px',
      padding: { xs: '16px 12px', sm: '18px 14px', md: '20px 16px' },
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      display: 'flex',
      flexDirection: 'column',
      margin: '0 auto',
      cursor: 'pointer',
    }}>
      <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, alignItems: 'center', mb: { xs: 1, sm: 1.5 } }}>
        <Avatar src={getDoctorPhotoUrl(doctor.image) || undefined} sx={{
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          flexShrink: 0,
        }}>
          {doctor.name?.charAt(0) || '?'}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600, color: '#007AFF', mb: 0.3 }}>
            {specNames}
          </Typography>
          <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 600, color: '#212121' }}>
            {doctor.name}
          </Typography>
        </Box>
      </Box>

      {shortDesc && (
        <Typography sx={{
          fontSize: { xs: 13, sm: 14 },
          color: 'rgba(33,33,33,0.7)',
          lineHeight: 1.4,
          mb: 0.625,
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {shortDesc}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          mb: { xs: 1.5, sm: 2 },
          '&:active': { opacity: 0.6 },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 13, sm: 14 },
            fontWeight: 600,
            color: '#007AFF',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Подробнее
        </Typography>

        <ArrowOutwardIcon
          sx={{
            color: '#007AFF',
            fontSize: { xs: 16, sm: 18 },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: { xs: '8px', sm: '10px' }, mt: 'auto' }}>
        <Button
          variant="outlined"
          onClick={() => {
            if (doctor.link) {
              window.open(doctor.link, '_blank');
            }
          }}
          sx={{
            flex: 1,
            height: { xs: 44, sm: 48 },
            borderRadius: '24px',
            borderColor: '#1677FF',
            color: '#1677FF',
            textTransform: 'none',
            fontSize: { xs: 13, sm: 14 },
            fontWeight: 600,
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            '&:hover': { borderColor: '#1677FF', backgroundColor: 'rgba(22,119,255,0.04)' },
          }}
        >
          Оставить отзыв
        </Button>
        <Button
          variant="contained"
          onClick={() => onBook(doctor.id)}
          disabled={isSubmitting}
          sx={{
            flex: 1,
            height: { xs: 44, sm: 48 },
            borderRadius: '24px',
            backgroundColor: '#1677FF',
            textTransform: 'none',
            fontSize: { xs: 13, sm: 14 },
            fontWeight: 600,
            boxShadow: 'none',
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            '&:hover': { backgroundColor: '#0055CC', boxShadow: 'none' },
          }}
        >
          {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Записаться'}
        </Button>
      </Box>
    </Box>
  );
};

// ОСНОВНОЙ КОМПОНЕНТ
export const DoctorsList: React.FC = () => {
  const navigate = useNavigate();

  const { clinic, branches } = useClinic();
  const { user } = useAuthStore();

  const currentBranch = branches.find(b => b.id === user?.branchId);
  const clinicName = currentBranch?.name || clinic?.name || 'Клиника';
  const workSchedule = currentBranch?.workSchedule || null;

  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [allDoctors, setAllDoctors] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Экран 1: фильтр специализаций
  const [specFilterIds, setSpecFilterIds] = useState<Set<string>>(new Set());
  const [showSpecsPopup, setShowSpecsPopup] = useState(false);

  // Экран 2: врачи по специализации
  const [activeSpec, setActiveSpec] = useState<Specialization | null>(null);
  const [catFilterIds, setCatFilterIds] = useState<Set<string>>(new Set());
  const [showCatsPopup, setShowCatsPopup] = useState(false);

  // Экран 3: детальная страница врача
  const [detailDoctor, setDetailDoctor] = useState<Doctor | null>(null);
  const [fullDoctor, setFullDoctor] = useState<Doctor | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Состояния для записи
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await doctorsService.getAllDoctors();
        const normalized = (result.doctors || []).map(normalizeDoctor);
        setAllDoctors(normalized);

        const map = new Map<string, Specialization>();
        normalized.forEach(d => {
          d.specializations?.forEach((s: Specialization) => {
            if (s.id && !map.has(s.id)) map.set(s.id, { id: s.id, name: s.name });
          });
        });
        setSpecializations(Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru')));
      } catch {
        setError('Не удалось загрузить данные');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const toggleSpecFilter = (id: string) => {
    setSpecFilterIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleCatFilter = (id: string) => {
    setCatFilterIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const visibleSpecs = specFilterIds.size > 0
    ? specializations.filter(s => specFilterIds.has(s.id))
    : specializations;

  const doctorsBySpec = activeSpec
    ? allDoctors.filter(d => d.specializations?.some(s => s.id === activeSpec.id))
    : [];

  const filteredDoctors = catFilterIds.size > 0
    ? doctorsBySpec.filter(d => d.categories?.some((c: Category) => catFilterIds.has(c.id)))
    : doctorsBySpec;

  const availableCategories: Category[] = (() => {
    if (!activeSpec) return [];
    const map = new Map<string, Category>();
    doctorsBySpec.forEach(d => {
      d.categories?.forEach((c: Category) => {
        if (c.id && !map.has(c.id)) map.set(c.id, { id: c.id, name: c.name });
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  })();

  const handleBook = async (doctorId: string) => {
    setIsSubmitting(true);
    try {
      await createAppointmentRequest({ doctorId, from: 'webapp' });
      setDetailDoctor(null);
      setShowSuccessPopup(true);
    } catch {
      setErrorMessage('Ошибка отправки заявки. Пожалуйста, попробуйте позже.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDetails = async (doctor: Doctor) => {
    setDetailDoctor(doctor);
    setFullDoctor(null);
    setIsLoadingDetails(true);
    try {
      const data = await doctorsService.getDoctor(doctor.id);
      if (data?.doctor) setFullDoctor(normalizeDoctor(data.doctor));
    } catch {
      // fallback на данные из списка
    } finally {
      setIsLoadingDetails(false);
    }
  };

  if (isLoading) return <Loader message="Загрузка врачей..." />;
  if (error) return <Typography color="error" sx={{ textAlign: 'center', mt: 4 }}>{error}</Typography>;

  // ЭКРАН 3: СТРАНИЦА ВРАЧА
  if (detailDoctor) {
    return (
      <>
        <DoctorDetailCard
          doctor={detailDoctor!}
          fullDoctor={fullDoctor}
          isLoadingDetails={isLoadingDetails}
          onBack={() => { setDetailDoctor(null); setFullDoctor(null); }}
          onBook={handleBook}
          isSubmitting={isSubmitting}
          clinicName={clinicName}
          workSchedule={workSchedule}
        />
        <SuccessPopup
          open={showSuccessPopup}
          onClose={() => setShowSuccessPopup(false)}
          clinicName={clinicName}
          workSchedule={workSchedule}
        />
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={4000}
          onClose={() => setErrorMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // ЭКРАН 2: ВРАЧИ ПО СПЕЦИАЛИЗАЦИИ
  if (activeSpec) {
    const catLabel = catFilterIds.size === 0
      ? 'Все врачи'
      : catFilterIds.size === 1
        ? availableCategories.find(c => catFilterIds.has(c.id))?.name || 'Все врачи'
        : `Выбрано: ${catFilterIds.size}`;

    return (
      <Box>
        <BackButton onClick={() => { setActiveSpec(null); setCatFilterIds(new Set()); }} />
      <Typography sx={{ fontSize: 36, fontWeight: 600, mb: 1, lineHeight: 1.1 }}>{activeSpec.name}</Typography>

        {availableCategories.length > 1 && (
          <PopupTrigger label={catLabel} onClick={() => setShowCatsPopup(true)} />
        )}
        <PopupMultiSelect
          open={showCatsPopup} onClose={() => setShowCatsPopup(false)}
          items={availableCategories} selectedIds={catFilterIds}
          onToggle={toggleCatFilter} onSelectAll={() => setCatFilterIds(new Set())}
          allLabel="Все врачи"
        />

        {filteredDoctors.length === 0 ? (
          <Typography sx={{ textAlign: 'center', mt: 4, color: 'rgba(33,33,33,0.5)' }}>Врачи не найдены</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredDoctors.map(doctor => (
              <DoctorCard key={doctor.id} doctor={doctor} onDetails={handleOpenDetails} onBook={handleBook} isSubmitting={isSubmitting} />
            ))}
          </Box>
        )}

        <SuccessPopup
          open={showSuccessPopup}
          onClose={() => setShowSuccessPopup(false)}
          clinicName={clinicName}
          workSchedule={workSchedule}
        />
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={4000}
          onClose={() => setErrorMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ЭКРАН 1: СПЕЦИАЛИЗАЦИИ (плитки 177x162, 2 столбца)
  const specLabel = specFilterIds.size === 0
    ? 'Все специализации'
    : specFilterIds.size === 1
      ? specializations.find(s => specFilterIds.has(s.id))?.name || 'Все специализации'
      : `Выбрано: ${specFilterIds.size}`;

  return (
    <Box>
      <BackButton onClick={() => navigate('/')} />
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '768px' } }}>
          <Typography sx={{ fontSize: { xs: 28, sm: 32, md: 36 }, fontWeight: 600, mb: 1, lineHeight: 1.1 }}>Врачи</Typography>
          <Typography sx={{ fontSize: { xs: 14, sm: 15, md: 16 }, fontWeight: 500, color: 'rgba(33,33,33,0.7)', mb: 2, lineHeight: 1.2 }}>
            Наша клиника предлагает помощь специалистов разных направлений
          </Typography>

          <PopupTrigger label={specLabel} onClick={() => setShowSpecsPopup(true)} />
        </Box>
      </Box>
      <PopupMultiSelect
        open={showSpecsPopup} onClose={() => setShowSpecsPopup(false)}
        items={specializations} selectedIds={specFilterIds}
        onToggle={toggleSpecFilter} onSelectAll={() => setSpecFilterIds(new Set())}
        allLabel="Все специализации"
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: { xs: '6px', sm: '8px', md: '12px' },
          justifyContent: 'center',
        }}
      >
        {visibleSpecs.map(spec => (
          <Box
            key={spec.id}
            onClick={() => { setActiveSpec(spec); setCatFilterIds(new Set()); }}
            sx={{
              width: '100%',
              height: {
                xs: '130px',
                sm: '145px',
                md: '160px',
                lg: '180px',
              },
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: {
                xs: '12px',
                sm: '14px',
                md: '16px',
              },
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'transform 0.2s',
              '&:active': { transform: 'scale(0.97)' },
            }}
          >
            <Box
              sx={{
                width: { xs: 36, md: 40 },
                height: { xs: 36, md: 40 },
                borderRadius: '50%',
                backgroundColor: '#007AFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getMedicalIcon(spec.name, 20)}
            </Box>
            <Typography
              sx={{
                fontSize: {
                  xs: '14px',
                  sm: '15px',
                  md: '16px',
                  lg: '18px',
                },
                fontWeight: 600,
                color: '#212121',
                lineHeight: 1.1,
              }}
            >
              {spec.name}
            </Typography>
          </Box>
        ))}
      </Box>

      <SuccessPopup
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        clinicName={clinicName}
        workSchedule={workSchedule}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};